/**
 * Account-scoped cache invalidation.
 *
 * A child-account swap re-authenticates in place with no page reload, so every
 * cache fetched under the previous account survives the switch and shows stale
 * data (most visibly the jobs grid). This clears all account-scoped caches and
 * refetches whatever is currently mounted. It runs from the `app:account-switched`
 * hook (subscribed in app/plugins/data-invalidation.client.ts, fired from
 * layers/auth/app/composables/useChildAccounts.ts `changeUser`).
 *
 * Two cache tiers exist:
 *  - Tier A: `useAsyncData` payload (every store built on `useStore`). A blanket
 *    `clearNuxtData` is safe because these are all account-scoped, except the
 *    parent's child-account list, which a swap never changes and whose loss
 *    would blank the account selector.
 *  - Tier B: plain `useState` caches that `clearNuxtData` never touches.
 *    `clearNuxtState` wipes a key to `undefined`, which is fine for caches whose
 *    consumers optional-chain a nullable value but crashes live Map/Set/object
 *    consumers, so those are reset to their initial value instead. Auth, config,
 *    theme and UI-filter state is account-independent and deliberately kept.
 */

export interface AccountSwitchedContext {
	/** hashID of the account switched TO. */
	toHashID: string;
	/** userID resolved after the switch, or null if the post-switch GetUser failed. */
	userID: string | null;
}

declare module '#app' {
	interface RuntimeNuxtHooks {
		'app:account-switched': (ctx: AccountSwitchedContext) => void | Promise<void>;
	}
}

/** Tier A `useAsyncData` keys to KEEP across a swap (parent-scoped, not account-scoped). */
export const PRESERVED_ASYNC_KEYS: ReadonlySet<string> = new Set(['lcp-child-accounts']);

/**
 * Tier B `useState` caches consumed as live Map/Set/object refs. Reset each to
 * the SAME initial value its owning store's `useState(key, factory)` uses, so
 * consumers never read the `undefined` that `clearNuxtState` would leave behind.
 * Keep these in lockstep with the stores (guarded by dataInvalidation.test.ts):
 *  - `lcp-units*`          layers/unit/app/stores/useUnitStore.ts
 *  - `lcp-favourite-locations`  layers/location/app/stores/useFavouriteLocationStore.ts
 *  - `lcp-combined-locations`   layers/location/app/composables/useCombinedLocations.ts
 *  - `lm-jobs-ui-state`    layers/limousine/app/composables/useLimoJobGrid.ts
 */
export const ACCOUNT_SCOPED_STATE_RESETTERS: Readonly<Record<string, () => unknown>> = {
	'lcp-units': () => null,
	'lcp-units-coords-cache': () => new Map(),
	'lcp-units-no-coords': () => new Set(),
	'lcp-units-prefetching': () => new Set(),
	'lcp-units-refresh-failures': () => 0,
	'lcp-favourite-locations': () => new Map(),
	'lcp-combined-locations': () => [],
	'lm-jobs-ui-state': () => ({ selectedJob: null }),
};

/**
 * Tier B key PREFIXES for dynamically-keyed caches that are null-initialised and
 * fully optional-chained by their consumers, so clearing them to `undefined` is
 * safe:
 *  - `lcp-job-*`         in-progress job create/edit forms (seeded from the account)
 *  - `last-job-quote-*`  the last computed booking quote
 */
export const ACCOUNT_SCOPED_STATE_PREFIXES: readonly string[] = ['lcp-job-', 'last-job-quote-'];

function isPrefixedAccountScopedStateKey(key: string): boolean {
	return ACCOUNT_SCOPED_STATE_PREFIXES.some((prefix) => key.startsWith(prefix));
}

/**
 * Drop every account-scoped cache, then refetch the stores that are currently
 * mounted so the visible view repopulates for the new account. Unmounted stores
 * refetch lazily on their next mount: `useStore` only serves a cached payload on
 * the initial mount, so a cleared key hits the network the next time it renders.
 */
export async function invalidateAccountScopedData(): Promise<void> {
	// Tier A: drop every useAsyncData payload except the parent account list.
	clearNuxtData((key) => !PRESERVED_ASYNC_KEYS.has(key));

	// Tier B: reset typed caches to their initial value (undefined would crash
	// live Map/Set/object consumers), then clear the null-safe prefixed ones.
	for (const [key, factory] of Object.entries(ACCOUNT_SCOPED_STATE_RESETTERS)) {
		useState(key).value = factory();
	}
	clearNuxtState(isPrefixedAccountScopedStateKey);

	// Repopulate what is on screen for the new account. refreshNuxtData only
	// refetches ACTIVE instances, so this is a no-op for closed panels/pages.
	await refreshNuxtData();
}
